import { Box, Grid, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ReportApi from 'api/report.api'
import ClinicApi from 'api/master/clinic.api'
import { getBranch, numberFormat } from 'utils/app.utils'
import { dateToView } from 'utils/date.utils'

import HeaderCard from 'component/CardCustom/HeaderCard'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import { colors } from 'constants/theme'

export default function VoucherPromotionSummary() {
  const { t } = useTranslation()
  const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [rangeDate, setRangeDate] = useState<string[]>(momentNowRange)
  const [loading, setLoading] = useState<boolean>(false)
  const [searchTrigger, setSearchTrigger] = useState(0)

  const [promotionRows, setPromotionRows] = useState<any[]>([])
  const [promotionGroup, setPromotionGroup] = useState<any[]>([])
  const [voucherRows, setVoucherRows] = useState<any[]>([])
  const [voucherGroup, setVoucherGroup] = useState<any[]>([])

  const promotionSummary = useMemo(() => {
    if (!promotionGroup?.length) return []
    return promotionGroup.flatMap((g: any) =>
      (g.promotions || []).map((p: any) => ({
        promotionTypeName: g.promotionTypeName,
        promotionName: p.promotionName,
        value: p.value,
        countUsed: p.countUsed || 0,
        sumDiscount: p.sumDiscount || 0
      }))
    )
  }, [promotionGroup])

  const voucherSummary = useMemo(() => {
    if (!voucherGroup?.length) return []
    return voucherGroup.flatMap((g: any) =>
      (g.vouchers || []).map((v: any) => ({
        voucherTypeName: g.voucherTypeName,
        voucherName: v.voucherName,
        value: v.value,
        countUsed: v.countUsed || 0,
        sumDiscount: v.sumDiscount || 0
      }))
    )
  }, [voucherGroup])

  const promotionTotals = useMemo(() => ({
    totalUsageCount: _.sumBy(promotionSummary, 'countUsed'),
    totalDiscount: _.sumBy(promotionSummary, 'sumDiscount')
  }), [promotionSummary])

  const voucherTotals = useMemo(() => ({
    totalUsed: _.sumBy(voucherSummary, 'countUsed'),
    totalUsedValue: _.sumBy(voucherSummary, 'sumDiscount')
  }), [voucherSummary])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [promoRes, vouchRes] = await Promise.all([
        ReportApi.reportPromotion({
          branchId,
          dateStart: rangeDate[0],
          dateEnd: rangeDate[1],
          page: 1,
          pageLimit: 999999,
          export: 1
        }),
        ReportApi.reportGiftVoucher({
          branchId,
          dateStart: rangeDate[0],
          dateEnd: rangeDate[1],
          page: 1,
          pageLimit: 999999,
          export: 1
        })
      ])
      setPromotionRows(promoRes?.data?.rows || [])
      setPromotionGroup(promoRes?.data?.group || [])
      setVoucherRows(vouchRes?.data?.rows || [])
      setVoucherGroup(vouchRes?.data?.group || [])
    } catch (e) {
      console.error('Error fetching promotion/voucher report:', e)
      setPromotionRows([])
      setPromotionGroup([])
      setVoucherRows([])
      setVoucherGroup([])
    } finally {
      setLoading(false)
    }
  }, [branchId, rangeDate])

  useEffect(() => {
    ClinicApi.findAllBranches().then((res) => setBranches(res.data)).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (searchTrigger >= 0) fetchData()
  }, [searchTrigger, fetchData])

  return (
    <Box>
      <HeaderCard text={t('รายงานสรุปโปรโมชั่นและวุเชอร์')} />
      <Box px={3} pt={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={branchId}
              onChange={e => setBranchId(Number(e.target.value))}
              options={branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={41}
              value={rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
              }}
              label={''}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type="primary"
              variant="contained"
              style={{ width: '120px' }}
              onClick={() => setSearchTrigger(prev => prev + 1)}
            />
          </Grid>
        </Grid>

        <Box className="mt-3 pl-xl- pr-xl-2 pb-4">
          {/* ========== สรุปการใช้โปรโมชั่น ========== */}
          <Box sx={{ mt: 2, mb: 3, width: '100%' }}>
            <Box sx={{ p: 2, backgroundColor: colors.themeMainColor + '10', borderRadius: '8px 8px 0 0', border: `1px solid ${colors.lightGray}`, borderBottom: 'none' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: colors.themeMainColor }}>
                    📊 สรุปการใช้โปรโมชั่น
                  </Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="textSecondary">จำนวนครั้งที่ใช้ (ในช่วงวันที่)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{numberFormat(promotionTotals.totalUsageCount, 0, 0)} ครั้ง</Typography>
                </Grid>
                <Grid item xs={6} md={2}>
                  <Typography variant="body2" color="textSecondary">ส่วนลดรวมทั้งหมด</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#c0392b' }}>{numberFormat(promotionTotals.totalDiscount)} ฿</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableCustom
                page={1}
                pageLimit={100}
                sortType="ASC"
                sortBy=""
                rowCount={promotionSummary.length}
                textEmptyData={t('ไม่มีข้อมูลโปรโมชั่น')}
                onSort={() => undefined}
                setPageLimit={() => undefined}
                setPage={() => undefined}
                tableMinWidth={900}
                headCells={[
                  { id: '1', labelKey: '#', align: 'center', width: '60px' },
                  { id: '2', labelKey: 'ประเภทโปรโมชั่น', align: 'left', width: '180px' },
                  { id: '3', labelKey: 'ชื่อโปรโมชั่น', align: 'left', width: 'auto' },
                  { id: '4', labelKey: 'เงื่อนไข/มูลค่า', align: 'left', width: '160px' },
                  { id: '5', labelKey: 'ใช้งาน (ครั้ง)', align: 'center', width: '120px' },
                  { id: '6', labelKey: 'ส่วนลดรวม (บาท)', align: 'right', width: '150px' }
                ]}
                rowsData={[
                  ...promotionSummary.map((promo: any, index: number) => (
                    <TableRowCommon
                      key={`promo-${promo.promotionTypeName}-${promo.promotionName}-${index}`}
                      id={index}
                      obj={promo}
                      columns={[
                        { option: 'TEXT', align: 'center', label: index + 1 },
                        { option: 'TEXT', align: 'left', label: promo.promotionTypeName, style: { fontWeight: 500, color: colors.themeMainColor } },
                        { option: 'TEXT', align: 'left', label: promo.promotionName },
                        { option: 'TEXT', align: 'left', label: promo.value ?? '-', style: { fontSize: '12px' } },
                        { option: 'TEXT', align: 'center', label: numberFormat(promo.countUsed, 0, 0), style: { fontWeight: 500 } },
                        { option: 'TEXT', align: 'right', label: numberFormat(promo.sumDiscount), style: { fontWeight: 600, color: '#c0392b' } }
                      ]}
                    />
                  )),
                  ...(promotionSummary.length > 0 ? [
                    <TableRowCommon
                      key="promo-total"
                      id="promo-total"
                      obj={{}}
                      columns={[
                        { option: 'MERGE', align: 'right', colSpan: 4, label: <Typography sx={{ fontWeight: 600 }}>{t('REPORT.TITLE.SUM')}</Typography> },
                        { option: 'TEXT', align: 'center', label: numberFormat(promotionTotals.totalUsageCount, 0, 0), style: { fontWeight: 600 } },
                        { option: 'TEXT', align: 'right', label: numberFormat(promotionTotals.totalDiscount), style: { fontWeight: 600, color: '#c0392b' } }
                      ]}
                      classes="row-summary"
                    />
                  ] : [])
                ]}
                hidePagination
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>รายละเอียดการใช้โปรโมชั่น</Typography>
              <TableCustom
                page={1}
                pageLimit={50}
                sortType="ASC"
                sortBy=""
                rowCount={promotionRows.length}
                textEmptyData={t('ไม่มีรายการใช้โปรโมชั่นในช่วงวันที่ที่เลือก')}
                onSort={() => undefined}
                setPageLimit={() => undefined}
                setPage={() => undefined}
                tableMinWidth={1200}
                headCells={[
                  { id: '1', labelKey: '#', align: 'center', width: '50px' },
                  { id: '2', labelKey: 'วันที่', align: 'center', width: '110px' },
                  { id: '3', labelKey: 'เลขที่ใบเสร็จ', align: 'left', width: '140px' },
                  { id: '4', labelKey: 'ชื่อผู้ป่วย', align: 'left', width: '180px' },
                  { id: '5', labelKey: 'รหัสผู้ป่วย', align: 'center', width: '110px' },
                  { id: '6', labelKey: 'รหัสสาขา', align: 'center', width: '100px' },
                  { id: '7', labelKey: 'ประเภทโปรโมชั่น', align: 'left', width: '140px' },
                  { id: '8', labelKey: 'ชื่อโปรโมชั่น', align: 'left', width: '180px' },
                  { id: '9', labelKey: 'ประเภท', align: 'center', width: '90px' },
                  { id: '10', labelKey: 'เงื่อนไข', align: 'left', width: '120px' },
                  { id: '11', labelKey: 'ส่วนลด (บาท)', align: 'right', width: '110px' }
                ]}
                rowsData={promotionRows.map((r: any, index: number) => (
                  <TableRowCommon
                    key={`promo-row-${r.promotionBranchId}-${index}`}
                    id={index}
                    obj={r}
                    columns={[
                      { option: 'TEXT', align: 'center', label: index + 1 },
                      { option: 'TEXT', align: 'center', label: dateToView(r.date) },
                      { option: 'TEXT', align: 'left', label: r.receiptNumber || '-' },
                      { option: 'TEXT', align: 'left', label: r.patientName || '-' },
                      { option: 'TEXT', align: 'center', label: r.cnNumber || '-' },
                      { option: 'TEXT', align: 'center', label: r.branchCnNumber || '-' },
                      { option: 'TEXT', align: 'left', label: r.promotionTypeName || '-' },
                      { option: 'TEXT', align: 'left', label: r.promotionName || '-' },
                      { option: 'TEXT', align: 'center', label: r.type || '-' },
                      { option: 'TEXT', align: 'left', label: r.promotionPrice ?? '-', style: { fontSize: '12px' } },
                      { option: 'TEXT', align: 'right', label: numberFormat(r.promotionDiscount), style: { color: '#c0392b' } }
                    ]}
                  />
                ))}
                hidePagination
              />
            </Box>
          </Box>

          {/* ========== สรุปการใช้ Gift Voucher ========== */}
          <Box sx={{ mt: 4, mb: 3, width: '100%' }}>
            <Box sx={{ p: 2, backgroundColor: '#4caf5010', borderRadius: '8px 8px 0 0', border: `1px solid ${colors.lightGray}`, borderBottom: 'none' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                    🎁 สรุปการใช้ Gift Voucher (ในช่วงวันที่)
                  </Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="body2" color="textSecondary">ใช้แล้ว (ครั้ง)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>{numberFormat(voucherTotals.totalUsed, 0, 0)} ครั้ง</Typography>
                </Grid>
                <Grid item xs={6} md={4}>
                  <Typography variant="body2" color="textSecondary">มูลค่าใช้ (บาท)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>{numberFormat(voucherTotals.totalUsedValue)} ฿</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ width: '100%', overflowX: 'auto' }}>
              <TableCustom
                page={1}
                pageLimit={100}
                sortType="ASC"
                sortBy=""
                rowCount={voucherSummary.length}
                textEmptyData={t('ไม่มีข้อมูล Gift Voucher')}
                onSort={() => undefined}
                setPageLimit={() => undefined}
                setPage={() => undefined}
                tableMinWidth={900}
                headCells={[
                  { id: '1', labelKey: '#', align: 'center', width: '60px' },
                  { id: '2', labelKey: 'ประเภท Voucher', align: 'left', width: '160px' },
                  { id: '3', labelKey: 'ชื่อ Voucher', align: 'left', width: 'auto' },
                  { id: '4', labelKey: 'มูลค่า/เงื่อนไข', align: 'left', width: '140px' },
                  { id: '5', labelKey: 'ใช้ (ครั้ง)', align: 'center', width: '100px' },
                  { id: '6', labelKey: 'ส่วนลดรวม (บาท)', align: 'right', width: '140px' }
                ]}
                rowsData={[
                  ...voucherSummary.map((v: any, index: number) => (
                    <TableRowCommon
                      key={`vouch-${v.voucherTypeName}-${v.voucherName}-${index}`}
                      id={index}
                      obj={v}
                      columns={[
                        { option: 'TEXT', align: 'center', label: index + 1 },
                        { option: 'TEXT', align: 'left', label: v.voucherTypeName, style: { fontWeight: 500, color: '#4caf50' } },
                        { option: 'TEXT', align: 'left', label: v.voucherName },
                        { option: 'TEXT', align: 'left', label: v.value ?? '-', style: { fontSize: '12px' } },
                        { option: 'TEXT', align: 'center', label: numberFormat(v.countUsed, 0, 0), style: { fontWeight: 500 } },
                        { option: 'TEXT', align: 'right', label: numberFormat(v.sumDiscount), style: { fontWeight: 600, color: '#2e7d32' } }
                      ]}
                    />
                  )),
                  ...(voucherSummary.length > 0 ? [
                    <TableRowCommon
                      key="voucher-total"
                      id="voucher-total"
                      obj={{}}
                      columns={[
                        { option: 'MERGE', align: 'right', colSpan: 4, label: <Typography sx={{ fontWeight: 600 }}>{t('REPORT.TITLE.SUM')}</Typography> },
                        { option: 'TEXT', align: 'center', label: numberFormat(voucherTotals.totalUsed, 0, 0), style: { fontWeight: 600 } },
                        { option: 'TEXT', align: 'right', label: numberFormat(voucherTotals.totalUsedValue), style: { fontWeight: 600, color: '#2e7d32' } }
                      ]}
                      classes="row-summary"
                    />
                  ] : [])
                ]}
                hidePagination
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>รายละเอียดการใช้ Gift Voucher</Typography>
              <TableCustom
                page={1}
                pageLimit={50}
                sortType="ASC"
                sortBy=""
                rowCount={voucherRows.length}
                textEmptyData={t('ไม่มีรายการใช้ Gift Voucher ในช่วงวันที่ที่เลือก')}
                onSort={() => undefined}
                setPageLimit={() => undefined}
                setPage={() => undefined}
                tableMinWidth={1200}
                headCells={[
                  { id: '1', labelKey: '#', align: 'center', width: '50px' },
                  { id: '2', labelKey: 'วันที่', align: 'center', width: '110px' },
                  { id: '3', labelKey: 'เลขที่ใบเสร็จ', align: 'left', width: '140px' },
                  { id: '4', labelKey: 'ชื่อผู้ป่วย', align: 'left', width: '180px' },
                  { id: '5', labelKey: 'รหัสผู้ป่วย', align: 'center', width: '110px' },
                  { id: '6', labelKey: 'รหัสสาขา', align: 'center', width: '100px' },
                  { id: '7', labelKey: 'รหัส Voucher', align: 'left', width: '140px' },
                  { id: '8', labelKey: 'ชื่อ Voucher', align: 'left', width: '180px' },
                  { id: '9', labelKey: 'มูลค่า/เงื่อนไข', align: 'left', width: '120px' },
                  { id: '10', labelKey: 'ส่วนลด (บาท)', align: 'right', width: '110px' }
                ]}
                rowsData={voucherRows.map((r: any, index: number) => (
                  <TableRowCommon
                    key={`vouch-row-${r.voucherListId}-${index}`}
                    id={index}
                    obj={r}
                    columns={[
                      { option: 'TEXT', align: 'center', label: index + 1 },
                      { option: 'TEXT', align: 'center', label: dateToView(r.date) },
                      { option: 'TEXT', align: 'left', label: r.receiptNumber || '-' },
                      { option: 'TEXT', align: 'left', label: r.patientName || '-' },
                      { option: 'TEXT', align: 'center', label: r.cnNumber || '-' },
                      { option: 'TEXT', align: 'center', label: r.branchCnNumber || '-' },
                      { option: 'TEXT', align: 'left', label: r.voucherCode || '-', style: { fontWeight: 500, color: '#4caf50' } },
                      { option: 'TEXT', align: 'left', label: r.voucherName || '-' },
                      { option: 'TEXT', align: 'left', label: r.voucherPrice ?? '-', style: { fontSize: '12px' } },
                      { option: 'TEXT', align: 'right', label: numberFormat(r.voucherDiscount), style: { color: '#2e7d32' } }
                    ]}
                  />
                ))}
                hidePagination
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Loading show={loading} type="fullLoading" />
    </Box>
  )
}
